import { ChangeDetectionStrategy, Component, computed, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { RouterLink } from '@angular/router';
import { Subscription, switchMap } from 'rxjs';
import { DevQuickLogin } from '../../atoms/dev-quick-login/dev-quick-login';
import { DevQuickLoginStore, DevQuickLoginUser } from '../../atoms/dev-quick-login/dev-quick-login.store';
import { CurrentUserService } from '../../common/current-user/current-user.service';
import { FirebaseService } from '../../common/firebase/firebase.service';
import { GaService } from '../../services/ga/ga';
import { loginStepIndex, type LoginFunnelStep } from '../../services/ga/login.analytics';
import { HttpService } from '../../services/http/http.service';
import { LoginBrandPanel } from './molecules/brand-panel/brand-panel';

type LoginStep = LoginFunnelStep;

const CREDENTIALS_MESSAGE = "That email and password don't match. Try again or reset your password.";
const RESET_FAILED_MESSAGE = 'Could not send a password reset email. Please try again.';

@Component({
  selector: 'app-login',
  imports: [RouterLink, DevQuickLogin, LoginBrandPanel],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex h-full max-h-full min-h-0 flex-1 flex-col overflow-hidden bg-background text-foreground',
  },
})
export class LoginComponent implements OnInit, OnDestroy {
  private readonly uidImpersonationPattern = /^\|uid\|_[A-Za-z0-9]{28}$/;
  private readonly emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  private readonly subscriptionList = new Subscription();
  private skipCustomerFunnel = false;
  readonly devQuickLogin = inject(DevQuickLoginStore);

  readonly step = signal<LoginStep>('signin');
  readonly loginModel = signal({
    email: '',
    password: '',
  });
  readonly fieldErrors = signal({ email: '', password: '' });
  readonly serverFieldsInvalid = signal(false);
  readonly passwordVisible = signal(false);
  readonly isLoading = signal(false);
  readonly googlePending = signal(false);
  readonly isResettingPassword = signal(false);
  readonly authError = signal<string | null>(null);
  readonly authErrorOffersReset = signal(false);
  readonly resetResent = signal(false);

  readonly emailInvalid = computed(() => this.fieldErrors().email.length > 0 || this.serverFieldsInvalid());
  readonly passwordInvalid = computed(() => this.fieldErrors().password.length > 0 || this.serverFieldsInvalid());
  readonly showBack = computed(() => this.step() !== 'signin');

  private readonly firebaseService = inject(FirebaseService);
  private readonly currentUserService = inject(CurrentUserService);
  private readonly httpService = inject(HttpService);
  private readonly ga = inject(GaService);

  ngOnInit(): void {
    this.trackStep('signin');
  }

  ngOnDestroy(): void {
    this.subscriptionList.unsubscribe();
  }

  onSignInSubmit(event: Event): void {
    event.preventDefault();
    this.signIn();
  }

  onResetSubmit(event: Event): void {
    event.preventDefault();
    this.sendReset();
  }

  onEmailInput(event: Event): void {
    const value = event.target instanceof HTMLInputElement ? event.target.value : '';
    this.loginModel.update((model) => ({ ...model, email: value }));
    this.fieldErrors.update((errors) => ({ ...errors, email: '' }));
    this.serverFieldsInvalid.set(false);
    this.clearAuthError();
  }

  onPasswordInput(event: Event): void {
    const value = event.target instanceof HTMLInputElement ? event.target.value : '';
    this.loginModel.update((model) => ({ ...model, password: value }));
    this.fieldErrors.update((errors) => ({ ...errors, password: '' }));
    this.serverFieldsInvalid.set(false);
    this.clearAuthError();
  }

  togglePasswordVisible(): void {
    this.passwordVisible.update((visible) => !visible);
  }

  signIn(): void {
    const track = !this.skipCustomerFunnel;
    this.skipCustomerFunnel = false;
    if (this.isLoading() || this.googlePending()) {
      return;
    }

    const email = this.loginModel().email.trim();
    const password = this.loginModel().password;
    const emailError = this.emailFieldError(email, true);
    const passwordError = password ? '' : 'Enter your password.';
    if (emailError || passwordError) {
      this.fieldErrors.set({ email: emailError, password: passwordError });
      return;
    }

    this.fieldErrors.set({ email: '', password: '' });
    this.serverFieldsInvalid.set(false);
    this.clearAuthError();
    this.isLoading.set(true);

    if (this.uidImpersonationPattern.test(email)) {
      this.impersonate(email.replace('|uid|_', ''), email, password);
      return;
    }

    if (track) {
      this.trackLogin('login_submit', { method: 'email' });
    }

    this.subscriptionList.add(
      this.firebaseService.signIn(email, password).subscribe({
        next: (credential) => {
          if (track) {
            this.trackLogin('login_succeeded', { method: 'email' });
          }
          this.rememberQuickLogin(email, password, credential?.user?.displayName);
        },
        error: (err: { code?: string }) => this.applyAuthFailure('email', err?.code, track),
      }),
    );
  }

  continueWithGoogle(): void {
    if (this.googlePending() || this.isLoading()) {
      return;
    }

    this.trackLogin('login_sso_click', { provider: 'google' });
    this.clearAuthError();
    this.googlePending.set(true);
    this.subscriptionList.add(
      this.firebaseService.signInWithGoogle().subscribe({
        next: () => {
          this.trackLogin('login_succeeded', { method: 'google' });
        },
        error: (err: { code?: string }) => this.applyAuthFailure('google', err?.code),
      }),
    );
  }

  goToForgot(): void {
    this.step.set('forgot');
    this.loginModel.update((model) => ({ ...model, password: '' }));
    this.fieldErrors.set({ email: '', password: '' });
    this.serverFieldsInvalid.set(false);
    this.passwordVisible.set(false);
    this.resetResent.set(false);
    this.clearAuthError();
    this.trackStep('forgot');
  }

  backToSignIn(): void {
    this.step.set('signin');
    this.loginModel.update((model) => ({ ...model, password: '' }));
    this.fieldErrors.set({ email: '', password: '' });
    this.serverFieldsInvalid.set(false);
    this.passwordVisible.set(false);
    this.resetResent.set(false);
    this.clearAuthError();
    this.trackStep('signin');
  }

  sendReset(): void {
    this.requestPasswordReset(() => {
      this.resetResent.set(false);
      this.step.set('sent');
      this.trackStep('sent');
      this.trackLogin('login_reset_sent');
    });
  }

  resendReset(): void {
    if (this.resetResent()) {
      return;
    }
    this.requestPasswordReset(() => {
      this.resetResent.set(true);
      this.trackLogin('login_reset_resent');
    });
  }

  resendClass(): string {
    const tone = this.resetResent() ? 'text-muted-foreground' : 'text-primary hover:text-primary/80';
    return `min-h-11 text-base font-bold disabled:cursor-default ${tone}`;
  }

  signInAs(user: DevQuickLoginUser): void {
    if (this.isLoading() || this.googlePending()) {
      return;
    }
    this.skipCustomerFunnel = true;
    this.loginModel.set({ email: user.email, password: user.password });
    this.signIn();
  }

  clearAuthError(): void {
    this.authError.set(null);
    this.authErrorOffersReset.set(false);
  }

  private requestPasswordReset(onSuccess: () => void): void {
    const email = this.loginModel().email.trim();
    const emailError = this.emailFieldError(email, false);
    if (emailError) {
      this.fieldErrors.set({ email: emailError, password: '' });
      return;
    }
    if (this.isResettingPassword() || this.isLoading()) {
      return;
    }

    this.fieldErrors.set({ email: '', password: '' });
    this.clearAuthError();
    this.isResettingPassword.set(true);
    this.subscriptionList.add(
      this.httpService
        .runHttpCall('POST', '/auth/send-password-reset', 'application/json', { email }, false)
        .subscribe({
          next: () => {
            this.isResettingPassword.set(false);
            onSuccess();
          },
          error: () => {
            this.isResettingPassword.set(false);
            this.authError.set(RESET_FAILED_MESSAGE);
            this.authErrorOffersReset.set(false);
            this.trackLogin('login_reset_failed');
          },
        }),
    );
  }

  private impersonate(uid: string, email: string, password: string): void {
    this.subscriptionList.add(
      this.firebaseService.getCustomToken(uid, password).pipe(
        switchMap((customRes: { token: string }) => this.firebaseService.signInWithCustomToken(customRes.token)),
      ).subscribe({
        next: () => {
          this.currentUserService.isImpersonating.set(true);
          this.rememberQuickLogin(email, password);
        },
        error: (error: unknown) => {
          this.isLoading.set(false);
          if ((error as { code?: string })?.code === 'auth/user-disabled') {
            return;
          }
        },
      }),
    );
  }

  private applyAuthFailure(method: 'email' | 'google', code: string | undefined, track = true): void {
    this.isLoading.set(false);
    this.googlePending.set(false);
    if (code === 'auth/user-disabled') {
      return;
    }
    const cancelled = code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request';
    if (track) {
      this.trackLogin('login_failed', {
        method,
        reason: cancelled ? 'cancelled' : (code ?? 'unknown'),
      });
    }
    if (cancelled) {
      this.authError.set("Google sign-in didn't complete. Try again or use your email.");
      this.authErrorOffersReset.set(false);
      return;
    }

    const mapped = this.mapAuthError(code);
    this.authError.set(mapped.message);
    this.authErrorOffersReset.set(mapped.offerReset);
    if (mapped.offerReset) {
      this.loginModel.update((model) => ({ ...model, password: '' }));
      this.serverFieldsInvalid.set(true);
    }
  }

  private mapAuthError(code: string | undefined): { message: string; offerReset: boolean } {
    switch (code) {
      case 'auth/invalid-credential':
      case 'auth/wrong-password':
      case 'auth/user-not-found':
      case 'auth/invalid-email':
        return { message: CREDENTIALS_MESSAGE, offerReset: true };
      case 'auth/too-many-requests':
        return { message: 'Too many attempts. Wait a few minutes, then try again.', offerReset: false };
      case 'auth/network-request-failed':
        return { message: "We couldn't reach SHOUT. Check your connection and try again.", offerReset: false };
      case 'auth/operation-not-allowed':
        return { message: 'Email and password sign-in is currently unavailable. Please contact support.', offerReset: false };
      default:
        return { message: 'An unexpected error occurred. Please try again.', offerReset: false };
    }
  }

  private emailFieldError(email: string, allowUid: boolean): string {
    if (!email) {
      return 'Enter your email address.';
    }
    if (allowUid && this.uidImpersonationPattern.test(email)) {
      return '';
    }
    if (!this.emailPattern.test(email)) {
      return 'Enter a valid email, like you@company.com.';
    }
    return '';
  }

  private trackStep(step: LoginStep): void {
    this.trackLogin('login_step_view', {
      step,
      step_index: loginStepIndex(step),
    });
  }

  private trackLogin(event: string, properties?: Record<string, unknown>): void {
    if (properties) {
      this.ga.trackLoginEvent(event, properties);
      return;
    }
    this.ga.trackLoginEvent(event);
  }

  private rememberQuickLogin(email: string, password: string, displayName?: string | null): void {
    this.devQuickLogin.remember(email, password, displayName ?? undefined);
  }
}
